import { LOCALES } from '@/shared/constants/common';

export type UTMParams = Record<string, string>;

const UTM_STORAGE_KEY = 'utm_params';
const UTM_STAMP_PREFIX = 'dev.2gis-';
const STAMPED_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];
const DIRECT_UTM_REFERRER = 'direct';

/** Сигнатуры продуктов, не совпадающие с первым сегментом пути (по нему — все остальные) */
const UTM_PRODUCTS: [path: string, product: string][] = [
  ['/api/mobile-sdk', 'mobile-sdk'],
  ['/2gis-geoflow-logistics', 'geoflow-logistics'],
];

const stripLocale = (pathname: string) => {
  const locale = Object.values(LOCALES).find(
    (value) => pathname === `/${value}` || pathname.startsWith(`/${value}/`),
  );

  return locale ? pathname.slice(locale.length + 1) || '/' : pathname;
};

export const getUtmProduct = (pathname: string): string => {
  const path = stripLocale(pathname);
  const known = UTM_PRODUCTS.find(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`),
  );

  return known?.[1] ?? (path.split('/').find(Boolean) || 'main');
};

/**
 * Ставит в source/medium/campaign `{метка}__dev.2gis-{продукт}`, без входящей метки — `dev.2gis-{продукт}`.
 * Уже проставленные суффиксы сохраняются, чтобы цепочка переходов не терялась.
 */
export const stampUTMParams = (
  utmParams: UTMParams,
  product: string,
): UTMParams => {
  const stamp = `${UTM_STAMP_PREFIX}${product}`;

  return {
    ...utmParams,
    ...Object.fromEntries(
      STAMPED_UTM_KEYS.map((key) => [
        key,
        utmParams[key] ? `${utmParams[key]}__${stamp}` : stamp,
      ]),
    ),
  };
};

const extractUTMFromURL = (): UTMParams => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  urlParams.forEach((value, key) => {
    if (!key.startsWith('utm_') || !value) return;

    utmParams[key] = value;
  });

  return utmParams;
};

const saveUTMParams = (utmParams: UTMParams): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  } catch (error) {
    void error;
  }
};

export const getUTMReferrerFromDocumentReferrer = (): string => {
  if (typeof document === 'undefined' || !document.referrer) {
    return DIRECT_UTM_REFERRER;
  }

  try {
    const referrerUrl = new URL(document.referrer);

    return (
      referrerUrl.hostname.toLowerCase().replace(/^www\./, '') ||
      document.referrer
    );
  } catch {
    return document.referrer;
  }
};

export const getSavedUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') return {};

  try {
    const saved = localStorage.getItem(UTM_STORAGE_KEY);

    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    void error;
    return {};
  }
};

export const clearUTMParams = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    void error;
  }
};

export const initializeUTMParams = (): UTMParams => {
  const currentUTM = extractUTMFromURL();

  if (Object.keys(currentUTM).length > 0) {
    saveUTMParams(currentUTM);
  }

  return getSavedUTMParams();
};
